import { lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { UserRead } from '@/api/types';
import { AI_DATA_CONSENT_REQUIRED_EVENT } from '@/api/client';
import { PageLoader } from '@/components/common/page-loader';
import { useAuth } from '@/hooks/use-auth';
import { clearUserAiQueryCache } from '@/lib/ai-query-cache';
import { queryClient } from '@/lib/query-client';
import { clearCoachClientState } from './coach-client-state';

const CoachProvider = lazy(() => import('./coach-runtime').then((module) => ({
  default: module.CoachProvider,
})));

export function CoachRuntimeBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { user, isAuthed } = useAuth();
  const isCoachPath = pathname === '/coach' || pathname.startsWith('/coach/');
  const [loaded, setLoaded] = useState(isCoachPath);

  const handleConsentRequired = useCallback(() => {
    const cachedUser = queryClient.getQueryData<UserRead>(['me']);
    if (cachedUser?.ai_data_consent === false) return;
    const userId = user?.id ?? cachedUser?.id;
    clearUserAiQueryCache(queryClient, userId);
    clearCoachClientState(userId);
    queryClient.setQueryData<UserRead>(['me'], (current) => (
      current ? { ...current, ai_data_consent: false } : current
    ));
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [user?.id]);

  useEffect(() => {
    if (isCoachPath) setLoaded(true);
  }, [isCoachPath]);

  useEffect(() => {
    window.addEventListener(AI_DATA_CONSENT_REQUIRED_EVENT, handleConsentRequired);
    return () => window.removeEventListener(
      AI_DATA_CONSENT_REQUIRED_EVENT,
      handleConsentRequired,
    );
  }, [handleConsentRequired]);

  const shouldLoad = loaded || isCoachPath;
  if (!shouldLoad || !isAuthed) return children;

  return (
    <Suspense fallback={<PageLoader />}>
      <CoachProvider
        key={user?.id ?? 'anonymous'}
        userId={user?.id}
        enabled={Boolean(user?.ai_data_consent)}
        onConsentRequired={handleConsentRequired}
      >
        {children}
      </CoachProvider>
    </Suspense>
  );
}
